import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";

async function getCroppedBlob(imageSrc, cropPixels, maxWidth = 1600) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = imageSrc;
  });
  const scale = Math.min(1, maxWidth / cropPixels.width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(cropPixels.width * scale);
  canvas.height = Math.round(cropPixels.height * scale);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    img,
    cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
    0, 0, canvas.width, canvas.height
  );
  return new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.88));
}

export default function ImageCropDialog({ file, aspect = 16 / 9, onCancel, onCropped }) {
  const [src] = useState(() => URL.createObjectURL(file));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_, croppedPixels) => setPixels(croppedPixels), []);

  const validate = async () => {
    if (!pixels) return;
    setBusy(true);
    const blob = await getCroppedBlob(src, pixels);
    URL.revokeObjectURL(src);
    onCropped(new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent className="max-w-2xl" data-testid="image-crop-dialog">
        <DialogHeader>
          <DialogTitle>Recadrez votre photo</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Déplacez l'image et ajustez le zoom pour choisir la zone visible (format panoramique 16:9).
        </p>
        <div className="relative w-full h-[340px] bg-ink/90 rounded-md overflow-hidden">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
          />
        </div>
        <div className="flex items-center gap-3 py-1">
          <span className="text-xs text-muted-foreground w-10">Zoom</span>
          <Slider value={[zoom]} min={1} max={4} step={0.05} onValueChange={([v]) => setZoom(v)} data-testid="crop-zoom-slider" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="crop-cancel-btn">
            Annuler
          </Button>
          <Button onClick={validate} disabled={busy || !pixels} className="bg-ink hover:bg-ink/85 text-cream" data-testid="crop-validate-btn">
            {busy ? "Traitement..." : "Valider le cadrage"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
